/*
 * @Author: songjingyang songjingyang@meishubao.com
 * @Date: 2025-03-28 15:40:04
 * @LastEditors: songjingyang songjingyang@meishubao.com
 * @LastEditTime: 2025-04-28 14:12:58
 * @FilePath: src/utils/request.ts
 * @Description: TypeScript版本 - 自动转换
 */

import axios from 'axios'
import { ElNotification, ElMessageBox, ElMessage, ElLoading, LoadingParentElement } from 'element-plus'
import { getToken } from '@/utils/auth'
import errorCode from '@/utils/errorCode'
import { tansParams, blobValidate } from '@/utils/ruoyi'
import cache from '@/plugins/cache'
import { saveAs } from 'file-saver'
import useUserStore from '@/store/modules/user'
import { isMiaoBiApp } from './ua'
import { ComponentPublicInstance, ComponentOptionsBase, Ref } from 'vue'
import { getCurrentLocale } from '@/locales'
import { translate } from '@/locales/route'

let downloadLoadingInstance: {
  close: any
  setText?: (text: string) => void
  removeElLoadingChild?: () => void
  handleAfterLeave?: () => void
  vm?: ComponentPublicInstance<{}, {}, {}, {}, {}, {}, {}, {}, false, ComponentOptionsBase<any, any, any, any, any, any, any, any, any, {}, {}, string, {}>, {}, {}>
  $el?: HTMLElement
  originalPosition?: Ref<string>
  originalOverflow?: Ref<string>
  visible?: Ref<boolean>
  parent?: Ref<LoadingParentElement>
  background?: Ref<string>
  svg?: Ref<string>
  svgViewBox?: Ref<string>
  spinner?: Ref<string | boolean>
  text?: Ref<string>
  fullscreen?: Ref<boolean>
  lock?: Ref<boolean>
  customClass?: Ref<string>
  target?: Ref<HTMLElement>
  beforeClose?: Ref<(() => boolean) | undefined> | undefined
  closed?: Ref<(() => void) | undefined> | undefined
}
// 是否显示重新登录
export let isRelogin = { show: false }

axios.defaults.headers['Content-Type'] = 'application/json;charset=utf-8'
// 创建axios实例
const service = axios.create({
  // axios中请求配置有baseURL选项，表示请求URL公共部分
  baseURL: import.meta.env.VITE_APP_BASE_API || '/api',
  // 超时
  timeout: 10 * 60 * 1000, // 10分钟
})

// request拦截器
service.interceptors.request.use(
  config => {
    if (config?.url?.includes('/clientApi')) {
      const SDKAppID = import.meta.env.VITE_APP_TENCENT_IM_SDK_APP_ID
      const SDKAppID_MIAOBI = import.meta.env.VITE_APP_TENCENT_IM_SDK_APP_ID_MIAOBI
      config.baseURL = ''
      config.headers = {
        ...config.headers,
        imAppId: isMiaoBiApp() ? SDKAppID_MIAOBI : SDKAppID,
      }
    }
    if (config?.url?.includes('/bytedance')) {
      config.baseURL = ''
    }
    config.headers = {
      ...config.headers,
      'Accept-Language': getCurrentLocale(),
    }
    // 是否需要设置 token
    const isToken = (config.headers || {}).isToken === false
    // 是否需要防止数据重复提交
    const isRepeatSubmit = (config.headers || {}).repeatSubmit === false
    if (getToken() && !isToken && !config?.url?.includes('/clientApi')) {
      config.headers['Authorization'] = 'Bearer ' + getToken() // 让每个请求携带自定义token 请根据实际情况自行修改
    }
    // get请求映射params参数
    const needOrgId = (config.headers || {}).needOrgId
    if (config.method === 'get' && config.params) {
      let params = needOrgId ? { ...config.params, orgId: config.params?.orgId ? config.params?.orgId : useUserStore().orgId } : config.params
      let url = config.url + '?' + tansParams(params)
      url = url.slice(0, -1)
      config.params = {}
      config.url = url
    }
    if (!isRepeatSubmit && (config.method === 'post' || config.method === 'put')) {
      config.data = needOrgId
        ? {
            ...config.data,
            orgId: Object.prototype.hasOwnProperty.call(config.data, 'orgId') ? config?.data?.orgId : useUserStore().orgId,
          }
        : config.data
      const requestObj = {
        url: config.url,
        data: typeof config.data === 'object' ? JSON.stringify(config.data) : config.data,
        time: new Date().getTime(),
      }
      const requestSize = Object.keys(JSON.stringify(requestObj)).length // 请求数据大小
      const limitSize = 5 * 1024 * 1024 // 限制存放数据5M
      if (requestSize >= limitSize) {
        console.warn(`[${config.url}]: ` + '请求数据大小超出允许的5M限制，无法进行防重复提交验证。')
        return config
      }
      // const sessionObj = cache.session.getJSON("sessionObj");
      // if (
      //   sessionObj === undefined ||
      //   sessionObj === null ||
      //   sessionObj === ""
      // ) {
      //   cache.session.setJSON("sessionObj", requestObj);
      // } else {
      //   const s_url = sessionObj.url; // 请求地址
      //   const s_data = sessionObj.data; // 请求数据
      //   const s_time = sessionObj.time; // 请求时间
      //   const interval = 1000; // 间隔时间(ms)，小于此时间视为重复提交
      //   if (
      //     s_data === requestObj.data &&
      //     requestObj.time - s_time < interval &&
      //     s_url === requestObj.url
      //   ) {
      //     const message = "数据正在处理，请勿重复提交";
      //     console.warn(`[${s_url}]: ` + message);
      //     return Promise.reject(new Error(message));
      //   } else {
      //     cache.session.setJSON("sessionObj", requestObj);
      //   }
      // }
    }
    return config
  },
  error => {
    console.log(error)
    Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  res => {
    // 未设置状态码则默认成功状态
    const code = res.data.code || 200
    // 获取错误信息
    const msg = errorCode[code] || res.data.msg || errorCode['default']
    // 二进制数据则直接返回
    if (res.request.responseType === 'blob' || res.request.responseType === 'arraybuffer') {
      return res.data
    }
    if (code === 401) {
      if (!isRelogin.show) {
        isRelogin.show = true
        ElMessageBox.confirm(translate('request.relogin.message'), translate('modal.systemPrompt'), {
          confirmButtonText: translate('request.relogin.confirm'),
          cancelButtonText: translate('common.cancel'),
          type: 'warning',
        })
          .then(() => {
            isRelogin.show = false
            useUserStore()
              .logOut()
              .then(() => {
                location.href = '/'
              })
          })
          .catch(() => {
            isRelogin.show = false
          })
      }
      return Promise.reject(translate('request.relogin.expired'))
    } else if (code === 500) {
      ElMessage({ message: msg, type: 'error' })
      return Promise.reject(new Error(msg))
    } else if (code === 601) {
      ElMessage({ message: msg, type: 'warning' })
      return Promise.reject(new Error(msg))
    } else if (code !== 200) {
      ElNotification.error({ title: msg })
      return Promise.reject('error')
    } else {
      return Promise.resolve(res.data)
    }
  },
  error => {
    console.log('err=====>' + error)
    let { message } = error
    if (message == 'Network Error') {
      message = translate('request.network.error')
    } else if (message.includes('timeout')) {
      message = translate('request.network.timeout')
    } else if (message.includes('Request failed with status code')) {
      message = translate('request.network.status', { status: message.substr(message.length - 3) })
    }
    ElMessage({ message: message, type: 'error', duration: 5 * 1000 })
    return Promise.reject(error)
  }
)

// 通用下载方法
export function download(url: any, params: any, filename: any, config: any): any {
  downloadLoadingInstance = ElLoading.service({
    text: translate('request.download.loading'),
    background: 'rgba(0, 0, 0, 0.7)',
  })
  return service
    .post(url, params, {
      transformRequest: [
        params => {
          return tansParams(params)
        },
      ],
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      responseType: 'blob',
      ...config,
    })
    .then(async data => {
      const isBlob = blobValidate(data)
      if (isBlob) {
        const blob = new Blob([data])
        saveAs(blob, filename)
      } else {
        const resText = await data.text()
        const rspObj = JSON.parse(resText)
        const errMsg = errorCode[rspObj.code] || rspObj.msg || errorCode['default']
        ElMessage.error(errMsg)
      }
      downloadLoadingInstance.close()
    })
    .catch(r => {
      console.error(r)
      ElMessage.error(translate('request.download.error'))
      downloadLoadingInstance.close()
    })
}

export default service
